const BASE = process.env.REACT_APP_BASE_URL;

const _getParamString = (obj) => {
  let str = "";
  for (const key in obj) {
    if (str !== "") {
      str += "&";
    }
    str += key + "=" + encodeURIComponent(obj[key]);
  }
  return str;
};

const formatResponse = async (res) => {
  if (res.status >= 200 && res.status < 300) {
    try {
      const data = await res.json();
      return { ...data, status: res.status };
    } catch (e) {
      return { status: res.status, data: null };
    }
  } else {
    let errorMessage = `HTTP ${res.status}`;
    try {
      const errorData = await res.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await res.text();
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
};

const getToken = () => {
  try {
    let accessToken = localStorage.getItem("accessToken");
    if (!accessToken || accessToken === "null") return null;
    try {
      const parsed = JSON.parse(accessToken);
      return parsed ? String(parsed) : null;
    } catch {
      return accessToken.replace(/^"(.*)"$/, "$1") || null;
    }
  } catch (error) {
    console.warn("Could not get accessToken from localStorage (private mode?):", error);
    return null;
  }
};

class ApiClient {
  static async get(url, params = {}, acceptOrOptions = "application/json") {
    let accept = "application/json";
    let externalSignal = null;
    if (typeof acceptOrOptions === "object" && acceptOrOptions !== null) {
      accept = acceptOrOptions.accept ?? "application/json";
      externalSignal = acceptOrOptions.signal ?? null;
    } else {
      accept = acceptOrOptions;
    }

    const paramString = _getParamString(params);
    url =
      Object.keys(params).length > 0
        ? `${BASE}${url}?${paramString}`
        : `${BASE}${url}`;
    
    const urlLower = url.toLowerCase();
    const timeout = urlLower.includes("v1/hotels-v2/hotelsearch")
      ? 180000
      : urlLower.includes("v1/hotels-v2/hoteldetails")
        ? 120000
        : urlLower.includes("v1/hotels-v2/searchhotelcity")
          ? 8000
          : 30000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort();
      } else {
        externalSignal.addEventListener("abort", () => controller.abort(), {
          once: true,
        });
      }
    }
    
    return fetch(`${url}`, {
      method: "GET",
      headers: {
        "Access-Control-Request-Method": "GET",
        Accept: accept,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        Authorization: "Bearer " + getToken(),
      },
      signal: controller.signal,
    })
      .then(async (res) => {
        clearTimeout(timeoutId);
        if (accept === "application/json") {
          return await formatResponse(res);
        }
        return res;
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        console.log("GET error:", err);
        throw err;
      });
  }

  static async put(url, params = {}, body = {}, accept = "application/json") {
    const paramString = _getParamString(params);
    url =
      Object.keys(params).length > 0
        ? `${BASE}${url}?${paramString}`
        : `${BASE}${url}`;

    return fetch(`${url}`, {
      method: "PUT",
      headers: {
        "Access-Control-Request-Method": "PUT",
        Accept: accept,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        Authorization: "Bearer " + getToken(),
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        return accept === "application/json" ? formatResponse(res) : res;
      })
      .catch((err) => console.log("error ", err));
  }

  static async delete(url, params = {}, accept = "application/json") {
    const paramString = _getParamString(params);
    url =
      Object.keys(params).length > 0
        ? `${BASE}${url}?${paramString}`
        : `${BASE}${url}`;

    return fetch(`${url}`, {
      method: "DELETE",
      headers: {
        "Access-Control-Request-Method": "DELETE",
        Accept: accept,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        Authorization: "Bearer " + getToken(),
      },
    })
      .then((res) => {
        return accept === "application/json" ? formatResponse(res) : res;
      })
      .catch((err) => console.log("error ", err));
  }

  static async patch(url, body = {}, accept = "application/json") {
    return fetch(`${BASE}${url}`, {
      method: "PATCH",
      headers: {
        "Access-Control-Request-Method": "PATCH",
        Accept: accept,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        Authorization: "Bearer " + getToken(),
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        return accept === "application/json" ? formatResponse(res) : res;
      })
      .catch((err) => {
        console.log("Patch call error:", err);
        throw err;
      });
  }

  static async post(url, body = {}, contentType = "application/json") {
    let finalBody = "";
    const headers = {
      "Access-Control-Request-Method": "POST",
      Accept: "application/json",
      "Access-Control-Allow-Origin": "*",
      Authorization: "Bearer " + getToken(),
    };

    if (contentType === "application/x-www-form-urlencoded") {
      for (const field in body) {
        finalBody += field + "=" + body[field] + "&";
      }
      headers["Content-Type"] = contentType;
    } else if (contentType === "application/json") {
      finalBody = JSON.stringify(body);
      headers["Content-Type"] = contentType;
    } else {
      finalBody = body;
    }
    url = BASE + url;

    const urlLower = url.toLowerCase();
    const isHotelSearch = urlLower.includes("v1/hotels-v2/hotelsearch");
    const isHotelStatic = urlLower.includes("v1/hotels-v2/hotelstaticdetails");
    const isHotelRooms = urlLower.includes("v1/hotels-v2/hotelrooms");
    const isHotelBlock = urlLower.includes("v1/hotels-v2/hotelblock");
    const isHotelPrice = urlLower.includes("v1/hotels-v2/hotelprice");
    const isHotelBook = urlLower.includes("v1/hotels-v2/hotelbook");
    const isHotelCancel = urlLower.includes("v1/hotels-v2/hotelcancel");
    const isNewsletterSubscribe = urlLower.includes("admin/subscriberreport");
    // hotelrooms: Nest may refresh Tripjack session then call SupplierAPIs (up to ~90s).
    // hotelprice: Nest may call HCI price twice for agents (up to ~90s each).
    // hotelblock: Hold may refresh supplier price before book.
    // hotelbook: Pay & Book runs Tripjack review/book (can exceed 70s after wallet pay).
    // hotelCancel: Tripjack/HCI cancel + wallet; keep under supplier limits but above old 70s default.
    const timeoutMs = isHotelSearch
      ? 300000
      : isHotelStatic
        ? 180000
        : isHotelRooms
        ? 120000
        : isHotelBlock
          ? 300000
          : isHotelPrice
            ? 120000
            : isHotelBook
              ? 300000
            : isHotelCancel
              ? 120000
            : isNewsletterSubscribe
              ? 180000
            : 70000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    return fetch(url, {
      method: "POST",
      body: finalBody,
      headers: headers,
      signal: controller.signal,
    })
      .then(async (res) => {
        clearTimeout(timeoutId);
        const text = await res.text();
        if (!text || !text.trim()) {
          return {
            status: res.status,
            message: res.ok
              ? "Empty response from server"
              : `Request failed (HTTP ${res.status})`,
          };
        }
        try {
          const data = JSON.parse(text);
          return { ...data, status: data?.status ?? res.status };
        } catch {
          return {
            status: res.status,
            message: text.slice(0, 300),
          };
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        console.log("Post call error : ", err);
        if (err?.name === "AbortError") {
          return {
            status: 408,
            message: `Request timed out after ${Math.round(timeoutMs / 1000)}s`,
          };
        }
        throw err;
      });
  }
}

export default ApiClient;
